import React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { Formik } from 'formik';

import { Divider } from '@yuhuinc/ui-library/';

import { media } from 'helpers/MediaHelper';
import { responsive } from 'helpers/StyleHelper';

import { StyledContainer, StyledRow } from '../common/LeaseStyles';
import { LeaseRejectedAlert } from '../common/LeaseRejectedAlert';
import { LeaseCreatorPageFooter } from '../common/LeaseCreatorPageFooter';
import { ServiceAndUtilityQuestion } from './ServiceAndUtilityQuestion';
import { VerticalFormWrapper } from 'components/ui/VerticalFormWrapper';

import { getTranslatedString } from 'helpers/i18n';
import { leaseCreatorColors } from 'helpers/ColorHelper';
import LeaseHeaderProgress from '../common/LeaseHeaderProgress';

const FORM_NAME = 'lease_service_and_utilities';

const StyledFormContainer = styled.div`
  background-color: white;
  width: 100%;
  margin-top: 24px;
  border: 1px solid ${leaseCreatorColors.FORM_BORDER};
  border-radius: 5px;
`;

const StyledForm = styled(VerticalFormWrapper)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  font-size: 24px;
  padding: 16px;
  max-width: 580px;
`;

const StyledPageHeader = styled.div`
  font-size: 24px;
  line-height: 32px;
  color: ${leaseCreatorColors.HEADER};
  font-weight: 400;
  margin-bottom: 16px;
`;

const StyledPageSubHeader = styled.div`
  font-size: 18px;
  line-height: 24px;
  color: ${leaseCreatorColors.HEADER};
  font-weight: 400;
  margin-bottom: 16px;
`;

const StyledHeaderDef = styled.div`
  font-size: 14px;
  line-height: 24px;
  color: ${leaseCreatorColors.HEADER};
  font-weight: 400;
  margin-bottom: 16px;
  ${media.mobile`
    font-size: ${responsive.text.mobile};
  `};
`;

const StyledDivider = styled(Divider)`
  margin: 13px 0px 28px;
`;

const LeaseServiceAndUtilitiesPage = ({
  forward,
  back,
  currentPage,
  totalPages,
  leaseFormValues,
  setLeaseFormValues,
  leaseDetails,
  handleCancellation,
  saveDraftLease,
  servicesForLease,
  utilitiesForLease,
  latestLeaseReview,
}) => {
  return (
    <StyledContainer>
      <LeaseHeaderProgress lease={leaseDetails} stepsFilled={currentPage} totalSteps={totalPages} />
      <Formik
        enableReinitialize
        initialValues={{
          services: servicesForLease || [],
          utilities: utilitiesForLease || [],
        }}
        onSubmit={(values) => {
          setLeaseFormValues((leaseFormValues) => ({
            ...leaseFormValues,
            ...values,
          }));
          saveDraftLease(values);
          forward();
        }}
      >
        {({ handleSubmit, values, setFieldValue }) => {
          return (
            <StyledFormContainer>
              <StyledForm id={FORM_NAME} onSubmit={handleSubmit}>
                <StyledPageHeader>
                  {getTranslatedString(
                    'landlordPortal.leaseCreatorV2.leaseServiceAndUtilitiesPage.servicesAndUtilities'
                  )}
                </StyledPageHeader>
                {latestLeaseReview?.status === 'rejected' && (
                  <StyledRow gutter={16}>
                    <LeaseRejectedAlert latestLeaseReview={latestLeaseReview} />
                  </StyledRow>
                )}
                {!isEmpty(values.services) || !isEmpty(values.utilities) ? (
                  <StyledHeaderDef>
                    {getTranslatedString(
                      'landlordPortal.leaseCreatorV2.leaseServiceAndUtilitiesPage.assignResponsibility'
                    )}
                  </StyledHeaderDef>
                ) : (
                  <StyledHeaderDef>
                    {getTranslatedString(
                      'landlordPortal.leaseCreatorV2.leaseServiceAndUtilitiesPage.noServiceOrUtility'
                    )}
                  </StyledHeaderDef>
                )}
                <React.Fragment>
                  <StyledPageSubHeader>
                    {getTranslatedString(
                      'landlordPortal.leaseCreatorV2.leaseServiceAndUtilitiesPage.whoPaysUtilities'
                    )}
                  </StyledPageSubHeader>
                  {!isEmpty(values.utilities) &&
                    values.utilities.map((parentQuestion, parentIndex) => (
                      <ServiceAndUtilityQuestion
                        values={values}
                        setFieldValue={setFieldValue}
                        parentQuestion={parentQuestion}
                        parentIndex={parentIndex}
                        serviceOrUtility="utilities"
                      />
                    ))}
                </React.Fragment>
                <StyledDivider dashed={false} plain={false} orientation="" type="horizontal" />
                <React.Fragment>
                  <StyledPageSubHeader>
                    {getTranslatedString(
                      'landlordPortal.leaseCreatorV2.leaseServiceAndUtilitiesPage.whatServicesAreIncluded'
                    )}
                  </StyledPageSubHeader>
                  {!isEmpty(values.services) &&
                    values.services.map((parentQuestion, parentIndex) => (
                      <ServiceAndUtilityQuestion
                        values={values}
                        setFieldValue={setFieldValue}
                        parentQuestion={parentQuestion}
                        parentIndex={parentIndex}
                        serviceOrUtility="services"
                      />
                    ))}
                </React.Fragment>
              </StyledForm>
            </StyledFormContainer>
          );
        }}
      </Formik>
      <LeaseCreatorPageFooter
        back={back}
        currentPage={currentPage}
        totalPages={totalPages}
        formName={FORM_NAME}
        handleCancellation={handleCancellation}
      />
    </StyledContainer>
  );
};

const mapStoreToProps = (store) => ({
  servicesForLease: store.landlord.leaseCreatorV2.servicesForLease,
  utilitiesForLease: store.landlord.leaseCreatorV2.utilitiesForLease,
  latestLeaseReview: store.landlord.leaseCreatorV2.latestLeaseReview,
});

const mapDispatchToProps = {};

export default connect(mapStoreToProps, mapDispatchToProps)(LeaseServiceAndUtilitiesPage);
